<template>
    <div v-if="translatable" class="v-input">
        <div class="grid-480 gap-2">
            <div v-for="language in all_languages">
                <input-with-validation
                    :type="type"
                    :main-div-class="mainDivClass"
                    :style-class="styleClass"
                    :errors="null"
                    :title="title+' - '+language.iso_code"
                    :value="(parsedVal && parsedVal[language.iso_code]) ? parsedVal[language.iso_code] : null"
                    :name="name+'.'+language.iso_code"
                    :disabled="disabled"
                    :checked="checked"
                    :name-type="nameType"
                    :label-class="labelClass"
                    :use-u-i-d="true"
                    :clear-button="clearButton"
                    :rows="rows"
                    :checkbox-message="checkboxMessage"
                    :translatable="false"
                    :autocomplete="autocomplete"
                    @onInput="translationChange($event, language.iso_code)"
                ></input-with-validation>
            </div>
        </div>

        <div class="row">
            <div class="col">
                <div class="custom-error-message" v-if="generateErrors().length > 0">
                    <template v-for="error in generateErrors()">
                        {{ error }}<br/>
                    </template>
                </div>
            </div>
        </div>
    </div>

    <div v-else :class="generateMainDiv()" class="v-input">
        <template v-if="nameType === 'label'  && !hideLabel">
            <label
                :class="labelClass"
                :for="id"
                v-if="title !== '' && type !== 'checkbox'"
            >
                {{ getLabelValue }}
            </label>
        </template>

        <template v-if="type === 'textarea'">
            <textarea
                :id="id"
                :name="name"
                :value="value"
                :disabled="disabled"
                @input="onInput"
                :class="generateClass()"
                :placeholder="placeholder"
                :rows="rows"
            ></textarea>
        </template>

        <template v-else-if="type === 'date'">
            <date-picker-primary
                :placeholder="placeholder"
                :value="value"
                :class="generateClass()"
                :config="{format: 'YYYY-MM-DD'}"
                :disabled="disabled"
                :errors="errors"
                @input="onInput"
            />

            <template v-if="clearButton">
                <div class="input-group" v-bind:class="{'is-invalid': hasError}">
                    <date-picker-primary
                        :placeholder="placeholder"
                        :value="value"
                        :class="generateClass()"
                        :config="{format: 'YYYY-MM-DD'}"
                        :errors="errors"
                        @input="onInput"
                    />
                    <div class="input-group-append" v-if="clearButton">
                        <button type="button" class="btn btn-danger" @click="onInput(null)">X</button>
                    </div>
                </div>
            </template>
        </template>

        <template v-else-if="type === 'checkbox'">
            <input
                :id="id"
                :name="name"
                type="checkbox"
                value="1"
                :disabled="disabled"
                :checked="checked"
                @input="onInput"
                :class="generateClass()"
            />

            <template v-if="!hideLabel">
                <label
                    :class="labelClass"
                    :for="id"
                    v-if="title !== '' && nameType === 'label'"
                >{{ title }}</label>
            </template>
        </template>

        <template v-else-if="type === 'checkbox-switch'">
            <checkbox-switch
                :id="id"
                :name="name"
                :checked="checked"
                @onInput="onInput"
            />
        </template>

        <template v-else-if="type === 'custom-checkbox'">
            <input
                :id="id"
                :name="name"
                type="checkbox"
                value="1"
                :disabled="disabled"
                :checked="checked"
                @input="onInput"
                :class="generateClass()"
            />
            <label :for="id"></label>

            <template v-if="!hideLabel">
                <label :for="id" class="mb-0 ml-2" v-html="checkboxMessage"></label>
                <a href="javascript:void(0)" :for="id" class="mb-0 ml-2" @click="onElementClick"
                   v-html="checkboxMessage2"></a>
            </template>
        </template>

        <template v-else-if="type === 'password'">
            <div class="password position-relative" v-bind:class="{'is-invalid': hasError}">
                <input
                    class="password"
                    :id="id"
                    :name="name"
                    :type="passwordShowing ? 'text' : 'password'"
                    :value="value"
                    :disabled="disabled"
                    :autocomplete="autocomplete ? 'on': 'off'"
                    @input="onInput"
                    :class="generateClass()"
                    :placeholder="placeholder"
                />

                <div class="">
                    <span v-if="passwordShowing" class="fa fa-eye password-toggler" @click="togglePasswordShow"></span>
                    <span v-else class="fa fa-eye-slash password-toggler" @click="togglePasswordShow"></span>
                </div>
            </div>
        </template>

        <template v-else-if="type === 'tinymce'">
            <tinymce
                :id="id.toString()"
                :value="value !== null ? value : ''"
                @editorInit="onEditorInit"
                @input="(event) => { onInput({target: {value: event}}) }"
            ></tinymce>
        </template>

        <template v-else>
            <input
                :id="id"
                :name="name"
                :type="type"
                :value="value"
                :disabled="disabled"
                @input="onInput"
                :class="generateClass()"
                :autocomplete="autocomplete ? 'on': 'off'"
                :placeholder="placeholder"
            />
        </template>

        <div class="invalid-feedback" v-if="generateErrors().length > 0">
            <template v-for="(error,index) in generateErrors()">
                <template v-if="index < 1">
                    {{ error }}<br/>
                </template>
            </template>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    .password-toggler {
        position: absolute;
        right: 0.5rem;
        top: 50%;
        transform: translateY(-50%);
    }
</style>

<script>
    import {mapGetters, mapActions} from 'vuex'
    import Vue from 'vue';
    import tinymce from 'vue-tinymce-editor'

    export default {
        name: 'input-with-validation',
        components: {
            tinymce
        },
        props: {
            type: {
                type: String,
                required: false,
                default: 'text'
            },
            mainDivClass: {
                type: String,
                required: false,
                default: ''
            },
            styleClass: {
                type: String,
                required: false,
                default: 'form-control m-0'
            },
            errors: {
                default: null
            },
            title: {
                type: String,
                required: false,
                default: ''
            },
            value: {
                default: function () {
                    if (this.type === "number") {
                        return 0;
                    }

                    return null;
                },
            },
            label: {
                type: String,
                required: false,
            },
            name: {
                type: String,
                required: false,
                default: ''
            },
            disabled: {
                type: Boolean,
                required: false,
                default: false
            },
            checked: {
                type: Boolean,
                required: false,
                default: false
            },
            nameType: {
                type: String,
                required: false,
                default: 'label'
            },
            labelClass: {
                type: String,
                required: false,
                default: ''
            },
            useUID: {
                type: Boolean,
                required: false,
                default: false,
            },
            clearButton: {
                type: Boolean,
                required: false,
                default: false,
            },
            rows: {
                type: String,
                required: false,
                default: ''
            },
            checkboxMessage: {
                type: String,
                required: false,
                default: ''
            },
            checkboxMessage2: {
                type: String,
                required: false,
            },
            translatable: {
                type: Boolean,
                required: false,
                default: false,
            },
            addColon: {
                type: Boolean,
                default: false,
            },
            required: {
                type: Boolean,
                default: false,
            },
            placeholder: {
                type: String,
                default: function () {
                    if (this.nameType === 'placeholder') {
                        return this.title;
                    }
                    if (this.title !== '') {
                        return (this.$t('general.enter') + " " + this.title.toLowerCase());
                    }

                    return '';
                },
            },
            hideLabel: {
                type: Boolean,
                default: false,
            },
            maxErrors: {
                type: Number,
                default: 100,
            },
            autocomplete: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            return {
                id: null,
                passwordShowing: false,
                parsedVal: {},
            }
        },
        created() {
            if(this.name !== '' && !this.useUID) {
                this.id = this.name;
            } else {
                this.id = this._uid;
            }
            this.parseJsonValue();
        },
        watch: {
            'value': function () {
                this.parseJsonValue();
            },
        },
        computed: {
            ...mapGetters('Language', ['all_languages']),
            getLabelValue: function () {
                if (this.label) {
                    return this.label;
                }

                let str = this.title;
                if (this.required) {
                    str += "*";
                }
                if (this.addColon) {
                    str += ":";
                }

                return str;
            },
        },
        methods: {
            parseJsonValue() {
                if (this.translatable) {
                    try {
                        if (typeof this.value === 'undefined' || this.value === null) {
                            throw new Error('');
                        }
                        this.parsedVal = JSON.parse(this.value);
                    } catch (e) {
                        this.parsedVal = {}
                    }
                }
            },
            onEditorInit(editor) {
                if(this.value !== null) {
                    editor.setContent(this.value);
                } else {
                    editor.setContent('');
                }
            },
            translationChange(event, iso_code) {
                Vue.set(this.parsedVal, iso_code, event.target.value);
                this.$emit('onInput', {target: {value: JSON.stringify(this.parsedVal)}});
            },
            onInput(event) {
                this.$emit('onInput', event);
            },
            generateClass() {
                let styleClass = {};

                if (this.type !== 'checkbox') {
                    styleClass[this.styleClass] = true;
                }
                if (this.name !== '') {
                    if (this.errors !== null && typeof this.errors[this.name] !== 'undefined' && this.errors[this.name] !== null) {
                        styleClass['is-invalid'] = true;
                    }
                }
                return styleClass;
            },
            hasError() {
                let err = false
                if (this.name !== '') {
                    if (this.errors !== null && typeof this.errors[this.name] !== 'undefined' && this.errors[this.name] !== null) {
                        err = true
                    }
                }
                return err;
            },
            togglePasswordShow() {
                if (this.passwordShowing) {
                    this.passwordShowing = false;
                } else {
                    this.passwordShowing = true;
                }
            },
            generateErrors() {
                let errors = [];
                if (this.errors !== null && typeof this.errors[this.name] !== 'undefined' && this.errors[this.name] !== null) {
                    errors = this.errors[this.name];
                }
                return errors;
            },
            generateMainDiv() {
                let styleClass = '';
                if (this.name !== '') {
                    if (this.errors !== null && typeof this.errors[this.name] !== 'undefined' && this.errors[this.name] !== null) {
                        styleClass += ' is-invalid';
                    }
                }

                return this.mainDivClass + styleClass;
            },
            onElementClick() {
                this.$emit("elementClick");
            },
        }
    }
</script>
